import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { ScreenLayout } from '@/components/shared/ScreenLayout';
import { Button } from '@/components/ui/Button';
import { AlertCircle } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getMaxBridge } from '@/lib/max-bridge';

/**
 * Экран 12 — Критическая ошибка.
 *
 * Источник: UX-спека v1.2 раздел Экран 12.
 *  - Иконка нейтральная (не паническая) — Lucide AlertCircle.
 *  - Заголовок «Что-то пошло не так».
 *  - Текст «Не удалось обработать ваш запрос. Попробуйте ещё раз через минуту».
 *  - Primary «Попробовать снова» → window.location.reload().
 *  - Secondary «Закрыть» → bridge.close().
 *
 * Когда показывается:
 *  - Невозможность восстановить сессию (повреждённый state).
 *  - Критический сбой расчёта результата (calculateStage1/2 throw).
 *  - 5xx от бэка (когда появится в Phase 4).
 *
 * Когда НЕ показывается:
 *  - Сбой alfaCRM — фоновая ошибка, retry в очереди (Phase 6).
 *  - Кратковременная потеря связи — inline-плашка (TODO Phase 5).
 *
 * Параметры error_type / screen_id передаются через router state из места
 * вызова (например, navigate('/error', { state: { errorType, screenId } })).
 *
 * Аналитика:
 *  - error_shown { error_type, screen_id } (mount).
 *  - error_retry (клик «Попробовать снова»).
 *  - error_close (клик «Закрыть»).
 */

interface ErrorRouterState {
  errorType?: string;
  screenId?: string;
}

export default function ErrorScreen() {
  const location = useLocation();
  const { track } = useAnalytics();
  const navState = (location.state as ErrorRouterState | null) ?? null;
  const errorType = navState?.errorType ?? 'unknown';
  const screenId = navState?.screenId ?? 'unknown';
  const isTemporarilyUnavailable = errorType === 'temporarily_unavailable';

  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    track('error_shown', { error_type: errorType, screen_id: screenId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    track('error_retry');
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  const handleClose = () => {
    track('error_close');
    try {
      getMaxBridge().close();
    } catch {
      // ignore — bridge может быть недоступен в тестах
    }
  };

  // Для kill switch (temporarily_unavailable) скрываем "Попробовать снова" —
  // retry бесполезен пока backend сам отказывается принимать заявки. Дать
  // ложную надежду на немедленный retry — хуже чем сказать "приходите позже".
  const cta = isTemporarilyUnavailable ? (
    <Button onClick={handleClose} fullWidth>
      Закрыть
    </Button>
  ) : (
    <div className="flex flex-col gap-2.5">
      <Button onClick={handleRetry} fullWidth>
        Попробовать снова
      </Button>
      <Button onClick={handleClose} fullWidth variant="secondary">
        Закрыть
      </Button>
    </div>
  );

  const heading = isTemporarilyUnavailable
    ? 'Приём заявок временно недоступен'
    : 'Что-то пошло не так';
  const description = isTemporarilyUnavailable
    ? 'Попробуйте позже. Мы скоро всё починим.'
    : 'Не удалось обработать ваш запрос. Попробуйте ещё раз через минуту.';

  return (
    <ScreenLayout cta={cta}>
      <div className="flex flex-1 flex-col items-center justify-center gap-5 py-8 text-center">
        <span
          aria-hidden="true"
          className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-text-secondary/10 text-text-secondary"
        >
          <AlertCircle size={48} strokeWidth={1.75} />
        </span>
        <h2 className="text-2xl font-bold leading-tight text-text-primary">
          {heading}
        </h2>
        <p className="max-w-prose text-base leading-relaxed text-text-secondary">
          {description}
        </p>
      </div>
    </ScreenLayout>
  );
}
